import { newCharacter } from './character';
import { hashString } from '../core/rng';
import { ACCOUNT_VERSION, type AccountSave, defaultSettings, newAccount } from './account';

/**
 * Local persistence: the whole roster is one JSON document in localStorage,
 * versioned with forward migrations. Export/import produce a checksummed
 * string so players can move saves between browsers.
 */

/** Unique on the shared github.io origin (another project there uses `aetherfall.*` keys). */
export const SAVE_KEY = 'aetherfall-online.account.v1';

type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

/** migrations[n] upgrades a save from version n to n + 1. */
const MIGRATIONS: Record<number, Migration> = {
  // Graphics quality gained 'auto' as the default; saves still on the old default (high) switch to it.
  1: (raw) => {
    const settings = raw.settings as Record<string, unknown> | undefined;
    if (settings?.quality === 'high') settings.quality = 'auto';
    return raw;
  },
};

export function migrate(raw: Record<string, unknown>): AccountSave {
  let v = typeof raw.version === 'number' ? raw.version : 0;
  let cur = raw;
  while (v < ACCOUNT_VERSION) {
    const m = MIGRATIONS[v];
    if (m) cur = m(cur);
    v++;
    cur.version = v;
  }
  // Fill any fields added after the save was written.
  const base = newAccount();
  const merged = { ...base, ...cur };
  merged.settings = { ...defaultSettings(), ...(merged.settings ?? {}) };
  merged.collectibles = { ...base.collectibles, ...(merged.collectibles ?? {}) };
  merged.haven = { ...base.haven, ...(merged.haven ?? {}) };
  merged.cards = { ...base.cards, ...(merged.cards ?? {}) };
  // Characters written before a field existed get its default.
  merged.characters = (merged.characters ?? []).map((c) => {
    try {
      return { ...newCharacter(c.id, c.name, c.classId), ...c };
    } catch {
      return c; // class not registered (tests) or unknown: keep as saved
    }
  });
  return merged;
}

export interface Storage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** In-memory storage used in tests and when localStorage is unavailable. */
export class MemoryStorage implements Storage {
  private m = new Map<string, string>();
  getItem(k: string): string | null {
    return this.m.get(k) ?? null;
  }
  setItem(k: string, v: string): void {
    this.m.set(k, v);
  }
  removeItem(k: string): void {
    this.m.delete(k);
  }
}

/** localStorage when the browser allows it, else memory (blocked site data, private modes). */
export function browserStorage(): Storage {
  try {
    const s = globalThis.localStorage;
    const probe = '__af_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return new MemoryStorage();
  }
}

export class SaveStore {
  private timer: ReturnType<typeof setTimeout> | null = null;
  lastSavedAt = 0;

  constructor(private storage: Storage = browserStorage()) {}

  load(): AccountSave {
    try {
      const raw = this.storage.getItem(SAVE_KEY);
      if (!raw) return newAccount();
      return migrate(JSON.parse(raw) as Record<string, unknown>);
    } catch (e) {
      console.warn('Save corrupted, starting fresh', e);
      return newAccount();
    }
  }

  save(a: AccountSave): void {
    try {
      this.storage.setItem(SAVE_KEY, JSON.stringify(a));
      this.lastSavedAt = Date.now();
    } catch (e) {
      console.warn('Save failed', e);
    }
  }

  /** Debounced save (coalesces bursts of changes). */
  saveSoon(a: AccountSave, delayMs = 800): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.save(a);
    }, delayMs);
  }

  wipe(): void {
    this.storage.removeItem(SAVE_KEY);
  }
}

function toBase64(text: string): string {
  let bin = '';
  for (const b of new TextEncoder().encode(text)) bin += String.fromCharCode(b);
  return btoa(bin);
}

function fromBase64(b64: string): string {
  const bin = atob(b64);
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}

export function exportSave(a: AccountSave): string {
  const payload = toBase64(JSON.stringify(a));
  return `AF1.${hashString(payload).toString(36)}.${payload}`;
}

export function importSave(text: string): AccountSave {
  const parts = text.trim().split('.');
  if (parts.length !== 3 || parts[0] !== 'AF1') throw new Error('Not an Aetherfall save code');
  const [, sum, payload] = parts as [string, string, string];
  if (hashString(payload).toString(36) !== sum) throw new Error('Save code is damaged (checksum mismatch)');
  return migrate(JSON.parse(fromBase64(payload)) as Record<string, unknown>);
}
